import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import dayjs from 'dayjs'
import { MedicationInfo } from 'components/Common/Medication/types'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import {
  createAnswerItemsFromValues,
  getAnswerValuesFromItems
} from 'components/Assessments/helpers'
import { formatAssessmentMedications } from 'components/Assessments/hooks/helpers'
import { AllQuestionnaireItemType } from 'components/Assessments/hooks/constants'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'
import { MedicationsFormField } from './formField'

/**
 * Medications subsection adapter for SOC Assessment
 * Handles medications extraction and transformation
 */
export class MedicationsAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    const medications = formatAssessmentMedications(flatAnswers)

    const medicationsSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_SUMMARY
    )?.at(0)

    const [initialsAuthorization] = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICATIONS_INITIALS_AUTHORIZATION
    )

    return {
      [MedicationsFormField.MEDICATIONS]: medications,
      [MedicationsFormField.MEDICATION_SUMMARY]: medicationsSummary,
      [MedicationsFormField.MEDICATIONS_INITIALS_AUTHORIZATION]: initialsAuthorization
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(MedicationsFormField.MEDICATION_SUMMARY)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_SUMMARY,
        value: this.getFormDataField(MedicationsFormField.MEDICATION_SUMMARY)
      })
    }

    // Handle medications list using existing pattern
    if (this.formDataFieldExists(MedicationsFormField.MEDICATIONS)) {
      const formMedications = this.getFormDataField(MedicationsFormField.MEDICATIONS) as
        | MedicationInfo[]
        | undefined
      if (formMedications && Array.isArray(formMedications)) {
        const medicationsAnswers = formMedications.map((medication) => {
          const questionnaireItemsAndValues: [AllQuestionnaireItemType, unknown][] = [
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DRUGBANK_PCID,
              medication.productConceptId
            ],
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_NAME,
              medication.name
            ],
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_ROUTE,
              medication.route
            ],
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_STRENGTH_NUMBER,
              medication.strength?.number
            ],
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_STRENGTH_UNIT,
              medication.strength?.unit
            ],
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_INDICATIONS,
              medication.indications
            ],
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_FREQUENCY,
              medication.frequency
            ],
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DOSAGE_NUMBER,
              medication.dosage?.number
            ],
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DOSAGE_UNIT,
              medication.dosage?.unit
            ],
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_INSTRUCTIONS,
              medication.instructions
            ],
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_USAGE_STATUS,
              medication.usageStatus
            ],
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_START_DATE,
              // Convert to UTC before formatting to ensure FHIR DATE fields (which are interpreted as UTC)
              medication.startDate && dayjs.isDayjs(medication.startDate)
                ? medication.startDate.utc().startOf('day').format('YYYY-MM-DD')
                : medication.startDate
            ],
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DISCONTINUE_DATE,
              // Convert to UTC before formatting to ensure FHIR DATE fields (which are interpreted as UTC)
              medication.discontinueDate && dayjs.isDayjs(medication.discontinueDate)
                ? medication.discontinueDate.utc().startOf('day').format('YYYY-MM-DD')
                : medication.discontinueDate
            ],
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_CONTROLLED,
              medication.controlled
            ],
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_LIQUID,
              medication.liquid
            ],
            [
              SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_INFECTION,
              medication.infection
            ]
          ]

          return {
            item: createAnswerItemsFromValues(questionnaireItemsAndValues)
          }
        })

        mutations.push({
          linkId: SocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO,
          value: medicationsAnswers
        })
      }
    }

    // Build initials authorization mutation
    if (this.formDataFieldExists(MedicationsFormField.MEDICATIONS_INITIALS_AUTHORIZATION)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICATIONS_INITIALS_AUTHORIZATION,
        value: this.getFormDataField(MedicationsFormField.MEDICATIONS_INITIALS_AUTHORIZATION)
      })
    }

    return mutations
  }
}
